import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listTransactions } from '@/api/transactions'
import type { RecurringItem } from '@/api/recurring'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { CategoryIcon } from '@/lib/categoryIcon'
import { currentPath, transactionsLink } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'
import { dayTitle } from './calendarModel'
import { ReminderRow } from './ReminderRow'

function dueLine(due: RecurringItem[], format: (n: number) => string): string {
  const out = due.filter((i) => i.type === 'EXPENSE').reduce((s, i) => s + i.amount, 0)
  const income = due.filter((i) => i.type === 'INCOME').reduce((s, i) => s + i.amount, 0)
  return [out > 0 && `Due ${format(out)}`, income > 0 && `Expected ${format(income)}`]
    .filter(Boolean)
    .join(' · ')
}

/** One day: what was spent (past) or what's due (future). */
export function DayPanel({
  date,
  today,
  spent,
  biggest,
  due,
  showTitle = true,
}: {
  date: string
  today: string
  spent: number
  biggest: boolean
  due: RecurringItem[]
  /** False inside a sheet, whose own title already names the day. */
  showTitle?: boolean
}) {
  const { format } = useFormatCurrency()
  const location = useLocation()
  const future = date > today
  const tx = useQuery({
    queryKey: ['transactions', 'day', date],
    queryFn: () => listTransactions({ date }),
    enabled: !future,
  })
  // A transfer is two rows; show it once, from the side the money left.
  const rows = (tx.data?.transactions ?? []).filter(
    (t) => !(t.type === 'TRANSFER' && t.transferDirection === 'IN')
  )

  return (
    <section aria-label={dayTitle(date)}>
      {showTitle && (
        <h2 className="font-serif text-xl font-semibold text-[var(--fl-heading)]">
          {dayTitle(date)}
        </h2>
      )}
      <p className="mt-0.5 mb-1.5 text-[12.5px] text-[var(--fl-text-2)]">
        {future
          ? due.length
            ? dueLine(due, format)
            : 'Nothing due'
          : spent > 0
            ? `Spent ${format(spent)}${biggest ? ' · your biggest day this month' : ''}`
            : 'No spending'}
      </p>
      {future ? (
        due.length > 0 && (
          <ul>
            {due.map((i) => (
              <ReminderRow key={i.id} item={i} date={date} today={today} />
            ))}
          </ul>
        )
      ) : tx.isError ? (
        <p className="py-3 text-sm text-[var(--fl-error)]">Couldn’t load this day.</p>
      ) : tx.isLoading ? (
        <p className="py-3 text-sm text-[var(--fl-text-2)]">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="py-3 text-sm text-[var(--fl-text-2)]">Nothing on this day.</p>
      ) : (
        <ul>
          {rows.map((t) => {
            const incoming =
              t.type === 'INCOME' || (t.type === 'TRANSFER' && t.transferDirection === 'IN')
            return (
              <li
                key={t.id}
                className="flex items-center gap-2.5 border-t border-[var(--fl-divider)] py-2 text-[13.5px] first:border-t-0"
              >
                <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-[var(--fl-hero-bg)] text-[var(--fl-hero-fg)]">
                  <CategoryIcon icon={t.categoryIcon ?? 'circle-dollar-sign'} size={14} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[var(--fl-text-1)]">{t.description}</span>
                  <span className="block truncate text-xs text-[var(--fl-text-2)]">
                    {[t.type === 'TRANSFER' ? 'Transfer' : t.categoryName, t.financialAccountName]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span
                  className={cn(
                    'shrink-0 font-semibold tabular-nums',
                    t.type === 'TRANSFER'
                      ? 'text-[var(--fl-text-2)]'
                      : incoming
                        ? 'text-[var(--fl-income)]'
                        : 'text-[var(--fl-error)]'
                  )}
                >
                  {incoming && t.type !== 'TRANSFER' ? '+' : ''}
                  {format(t.amount)}
                </span>
              </li>
            )
          })}
        </ul>
      )}
      {!future && (
        <div className="mt-2 text-right">
          <Link
            to={transactionsLink({ date }, { back: currentPath(location) })}
            className="text-[12.5px] text-[var(--fl-primary)] hover:underline"
          >
            Open in Transactions ›
          </Link>
        </div>
      )}
    </section>
  )
}
